/**
 * Kivi's geometry, ported number for number from the Kivi guide
 * (design/explorations/kivi/kivi-in-recordos.html, "the Kivi model").
 * Plain constants, so a worklet can capture them.
 */
import type { KiviCubic, KiviPivot, KiviPoint } from './types';

/** The disc: Kivi's body, in the 100 box. */
export const DISC =
  'M50 6C78.9 6 94.06 26.59 94.06 50C94.06 73.06 77.14 94 50 94C22.86 94 5.94 73.06 5.94 50C5.94 26.59 21.1 6 50 6Z';

/** The near and far eye centres. */
export const E1: KiviPoint = [40, 48.4];
export const E2: KiviPoint = [60.4, 45.53];
/** The small cut's eye centres. */
export const SMALL_EYES: readonly [KiviPoint, KiviPoint] = [
  [34.9, 49],
  [58.3, 46.13],
];

/** The eyes' base rotation, degrees. */
export const BR = -8;
/** Feature scale: the pebble's eye sizes, times this for the larger disc. */
export const FS = 1.25;

/** The line: one cubic out of the right of the face. */
export const LINE: KiviCubic = [
  [74.4, 43.6],
  [80.4, 41.9],
  [88.4, 36.9],
  [101.5, 27.6],
];

/** Where a full curl pulls the line's last two points. */
export const CURL_TO: readonly [KiviPoint, KiviPoint] = [
  [86.4, 33.3],
  [81, 30],
];

/** The tick of done: the down stroke, then the up stroke. */
export const TICK_A: KiviCubic = [
  [74.4, 43.6],
  [76.3, 46.5],
  [78.3, 49.5],
  [80.2, 52.4],
];
export const TICK_B: KiviCubic = [
  [80.2, 52.4],
  [87.2, 44.9],
  [95.2, 35.4],
  [105.2, 24.4],
];

/** The points Kivi leans on. */
export const PIV: { readonly [K in KiviPivot]: KiviPoint } = {
  front: [64, 93],
  back: [36, 93],
  centre: [50, 50],
  center: [50, 93],
};

export const TAU = Math.PI * 2;

/** The record pebbles of building, bottom first, before their offset. */
export const PEBBLES: readonly [string, string, string] = [
  'M74 88C70 88 68 85.5 68 83C68 79 72 76.5 79 76.5C86 76.5 92 79 92 83C92 86 89 88 85 88Z',
  'M76 76C73.5 76 72 74 72.5 72C73 69 76 67.5 80.5 67.5C85 67.5 88.5 69.5 88.5 72.5C88.5 74.5 86.5 76 84 76Z',
  'M78 67C76.5 67 75.5 66 75.8 64.6C76.2 62.6 78.2 61.6 80.8 61.6C83.4 61.6 85.6 62.8 85.6 64.8C85.6 66.2 84.4 67 83 67Z',
];

/** The lit body: the disc drawn again over the under colour, lifted by this. */
export const LIT_LIFT: KiviPoint = [-1, -4.6];
/** The lit highlight: an ellipse in light at 0.6, rotated about its centre. */
export const HIGHLIGHT = { cx: 36, cy: 21, rx: 23, ry: 8.5, rot: -24, opacity: 0.6 } as const;
/** The dark scheme's rim. */
export const RIM = { width: 1.6, opacity: 0.9 } as const;
/** The glow: a circle at the centre, solid to 0.55 of r at 0.34 times the glow, then fading out. */
export const GLOW = { cx: 50, cy: 50, r: 62, stop: 0.55, opacity: 0.34 } as const;
/** The floor shadow's centre line. */
export const SHADOW_CY = 96.2;

/**
 * The favicon (the guide's `favicon()`): one still drawing for 16 to 23 px
 * that fills its box edge to edge, with swollen lids and line, so every pale
 * part is 2 px at 16. Its own disc, wider than `DISC`, the two lids as paths
 * rotated about their own points and filled and stroked 2 wide with round
 * corners, and the line as one filled path, all clipped to the disc.
 */
export const FAVICON_DISC =
  'M50 2C81.53 2 98.06 24.46 98.06 50C98.06 75.15 79.61 98 50 98C20.39 98 1.94 75.15 1.94 50C1.94 24.46 18.47 2 50 2Z';
export const FAVICON_EYES: readonly { readonly d: string; readonly rotate: readonly [number, number, number] }[] = [
  { d: 'M16.5 53A10 12.5 0 0 0 36.5 53A10 1 0 0 1 16.5 53Z', rotate: [-8, 26.5, 53] },
  { d: 'M43 49.28A10 12.5 0 0 0 63 49.28A10 1 0 0 1 43 49.28Z', rotate: [-8, 53, 49.28] },
];
export const FAVICON_EYE_STROKE = 2;
export const FAVICON_LINE =
  'M76.02 52.03C81.68 50.37 91.47 45.4 105.38 36.26A6.25 6.25 0 0 0 98.62 25.74C84.53 34.6 78.32 37.63 72.58 38.97A6.75 6.75 0 0 0 76.02 52.03Z';

/** The app icon (the guide's `appIcon()`): a lapis tile, its corners 22.5 round, and the paper disc moved and scaled onto it. */
export const APP_ICON = { radius: 22.5, x: 14, y: 13, scale: 0.72 } as const;
